var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Heading from "./Heading";
import Footer from "./Footer";
import { Alert, Button, Card, CardContent, Container, Divider, LinearProgress, Stack, ThemeProvider, Typography, Box, Chip, CircularProgress, TextField, } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Autorenew";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import getTheme from "./Theme";
import { getLayoutInfo } from "./LayoutInfo";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { restoreSession, useProfileAuthStore } from "./store/ProfileStore";
import { sendAjaxRequest } from "./API";
import { ClientDetailCode } from "./interface";
import { SearchBar } from "./components/SearchBar";
import { UserAvatar } from "./components/UserAvatar";
import { ContentCardStacks } from "./components/ContentCardStacks";
import { listReports } from "./store/ReportStore";
import { ReportElement } from "./components/ReportElement";
import { forceLogin } from "./Auth/AuthUtils";
import { useCacheBusterStore } from "./util/UrlUtils";
import { setModerationReviewStatus, useModeratedPostStore, } from "./store/ModeratedPostStore";
import { useLogger } from "./logger";
import { ModeratedPostCard } from "./components/ModeratedPostCard";
import { ModerationActionDialog } from "./components/ModerationActionDialog";
const subHeadingColor = "#999999";
let timer = undefined;
let timerStartText = "";
export function Admin() {
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [searchResults, setSearchResults] = useState();
    const [reportsList, setReportsList] = useState(undefined);
    const [reportsLoading, setReportsLoading] = useState();
    const [reportsError, setReportsError] = useState("");
    // New state for moderation dialog and action tracking
    const [moderationDialog, setModerationDialog] = useState({
        open: false,
        action: null,
        filename: "",
        loading: false,
        success: false,
        error: null,
    });
    const [actionInProgress, setActionInProgress] = useState(null);
    // Ops dashboard state
    const [opsLoading, setOpsLoading] = useState(false);
    const [opsGenerating, setOpsGenerating] = useState(false);
    const [opsError, setOpsError] = useState("");
    const [opsHtml, setOpsHtml] = useState("");
    const [opsGeneratedAt, setOpsGeneratedAt] = useState("");
    const [opsAnalyzeTarget, setOpsAnalyzeTarget] = useState("");
    const [opsAnalyzeLoading, setOpsAnalyzeLoading] = useState(false);
    const [opsAnalyzeResult, setOpsAnalyzeResult] = useState(null); // eslint-disable-line @typescript-eslint/no-explicit-any
    const [opsAnalyzeError, setOpsAnalyzeError] = useState("");
    const opsIframeRef = useRef(null);
    // Polling control: bumps on each new Load/Refresh click so stale pollers
    // from a previous click become no-ops, and reset to 0 on unmount.
    const opsPollTokenRef = useRef(0);
    // Hard cap on how long we poll before surfacing a timeout error. The
    // dashboard build typically finishes in ~3-4 minutes; 10 minutes is a
    // generous safety margin that still protects against a wedged build.
    const OPS_POLL_MAX_ELAPSED_MS = 10 * 60 * 1000;
    const OPS_POLL_INTERVAL_MS = 10 * 1000;
    const modStore = useModeratedPostStore((state) => state);
    const navigate = useNavigate();
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const logger = useLogger((state) => state);
    const layout = getLayoutInfo();
    const loadModerationEntries = () => {
        logger.log("loadModerationEntries");
        modStore.loadModeratedPosts(auth, cacheBusterStore, logger);
    };
    const loadOpsDashboard = useCallback((refresh = false) => {
        logger.log("loadOpsDashboard, refresh=" + refresh);
        setOpsLoading(true);
        setOpsGenerating(false);
        setOpsError("");
        // Invalidate any in-flight poll from a prior click.
        opsPollTokenRef.current += 1;
        const pollToken = opsPollTokenRef.current;
        const pollStartMs = Date.now();
        const sendOpsRequest = (forceRefresh) => {
            const apiCallback = (ok, status, message) => {
                // A newer Load/Refresh click has taken over — drop this response.
                if (opsPollTokenRef.current !== pollToken) {
                    logger.log("loadOpsDashboard: stale response ignored");
                    return;
                }
                if (!ok) {
                    setOpsLoading(false);
                    setOpsGenerating(false);
                    try {
                        const result = JSON.parse(message);
                        setOpsError(result.error || "Unknown error");
                    }
                    catch (_a) {
                        setOpsError(message || "Failed to load ops dashboard");
                    }
                    return;
                }
                let result;
                try {
                    result = JSON.parse(message);
                }
                catch (_b) {
                    setOpsLoading(false);
                    setOpsGenerating(false);
                    setOpsError("Malformed dashboard response");
                    return;
                }
                if (result.status === "ready" ||
                    result.clientDetailCode === ClientDetailCode.OPS_DASHBOARD_READY) {
                    setOpsHtml(result.html || "");
                    setOpsGeneratedAt(result.generatedAt || "");
                    setOpsGenerating(false);
                    setOpsLoading(false);
                    return;
                }
                if (status === 202 ||
                    result.status === "generating" ||
                    result.clientDetailCode ===
                        ClientDetailCode.OPS_DASHBOARD_GENERATING) {
                    setOpsGenerating(true);
                    if (Date.now() - pollStartMs > OPS_POLL_MAX_ELAPSED_MS) {
                        setOpsLoading(false);
                        setOpsGenerating(false);
                        setOpsError("Dashboard generation timed out. Please try again in a few minutes.");
                        return;
                    }
                    // Subsequent polls never set refresh=true — we just want to pick
                    // up the cache once the background build finishes.
                    window.setTimeout(() => sendOpsRequest(false), OPS_POLL_INTERVAL_MS);
                    return;
                }
                setOpsLoading(false);
                setOpsGenerating(false);
                setOpsError(result.error || "Unexpected dashboard response");
            };
            const payload = {
                operation: "dashboard",
                bearer: auth.bearer,
                refresh: forceRefresh,
                sequence: auth.sequence,
            };
            sendAjaxRequest(apiCallback, "ops", payload, auth.bearer, cacheBusterStore, logger);
        };
        sendOpsRequest(refresh);
    }, [auth.bearer, auth.sequence, cacheBusterStore, logger]);
    // Cancel any in-flight poll when the component unmounts.
    useEffect(() => {
        return () => {
            opsPollTokenRef.current += 1;
        };
    }, []);
    const runOpsAnalyze = useCallback((target) => {
        if (!target.trim())
            return;
        logger.log("runOpsAnalyze: " + target);
        setOpsAnalyzeLoading(true);
        setOpsAnalyzeError("");
        setOpsAnalyzeResult(null);
        const apiCallback = (ok, _status, message) => {
            setOpsAnalyzeLoading(false);
            try {
                const result = JSON.parse(message);
                if (ok) {
                    setOpsAnalyzeResult(result.analysis);
                }
                else {
                    setOpsAnalyzeError(result.error || "Analysis failed");
                }
            }
            catch (_a) {
                setOpsAnalyzeError(message || "Failed to parse analysis response");
            }
        };
        const payload = {
            operation: "analyze",
            bearer: auth.bearer,
            target: target.trim(),
            timeRange: "7d",
            sequence: auth.sequence,
        };
        sendAjaxRequest(apiCallback, "ops", payload, auth.bearer, cacheBusterStore, logger);
    }, [auth.bearer, auth.sequence, cacheBusterStore, logger]);
    const openDashboardInNewTab = () => {
        if (!opsHtml)
            return;
        const blob = new Blob([opsHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    const toggleFilter = () => {
        const newIncludeReviewed = !modStore.includeReviewed;
        modStore.setIncludeReviewed(newIncludeReviewed);
        // Reload with the new filter setting
        modStore.loadModeratedPosts(auth, cacheBusterStore, logger, newIncludeReviewed);
    };
    const loadReports = () => {
        logger.log("loadReports");
        setReportsLoading(true);
        listReports({
            okCallback(clientDetailCode, reports) {
                setReportsLoading(false);
                if (reports) {
                    setReportsList(reports);
                }
            },
            errorCallback(clientDetailCode, message) {
                setReportsLoading(false);
                logger.error(message);
                setReportsError(message);
                setReportsList([]);
            },
        }, auth, cacheBusterStore, logger);
    };
    useEffect(() => {
        if (!auth.isSignedIn(logger) || !auth.userId) {
            if (!restoreSession(auth, logger)) {
                logger.log("User NOT logged in - navigating...");
                forceLogin(navigate);
            }
        }
        else if (auth.accountType != "admin") {
            logger.log("NOT admin user - navigating...");
            navigate("/");
        }
    }, [auth.isSignedIn(), auth.userId, auth.accountType]);
    // Load reports and moderated posts when the component mounts
    useEffect(() => {
        if (auth &&
            auth.isSignedIn() &&
            auth.accountType === "admin" &&
            !reportsLoading &&
            !reportsList) {
            loadReports();
        }
    }, [auth.isSignedIn(), auth.accountType, reportsLoading, reportsList]);
    useEffect(() => {
        if (auth &&
            auth.isSignedIn() &&
            auth.accountType === "admin" &&
            modStore &&
            !modStore.error &&
            !modStore.loading &&
            !modStore.moderatedPosts) {
            loadModerationEntries();
        }
        else {
            logger.log("Moderated posts already loading, loaded or error occurred.");
        }
    }, [
        auth.isSignedIn(),
        auth.accountType,
        modStore.error,
        modStore.loading,
        modStore.moderatedPosts,
    ]);
    const doSearch = (searchText, logger) => {
        logger.log("doSearch: " + searchText);
        if (!searchText || !searchText.trim()) {
            logger.log("Empty search text");
            return;
        }
        setSearching(true);
        const apiCallback = (ok, status, message) => {
            var _a;
            setSearching(false);
            setSearchError("");
            logger.log("searchCallback: " + message);
            try {
                if (ok) {
                    const result = JSON.parse(message);
                    if (result.bearer && result.sequence) {
                        auth.setBearerAndSequence(result.bearer, result.sequence, logger);
                    }
                    const searchEntries = [];
                    (_a = result.entries) === null || _a === void 0 ? void 0 : _a.forEach((entry) => {
                        if (entry.userId != auth.userId) {
                            searchEntries.push(entry);
                        }
                    });
                    setSearchResults(searchEntries);
                }
                else {
                    const result = JSON.parse(message);
                    setSearchError(result.error + " (" + result.clientDetailCode + ")");
                }
            }
            catch (err) {
                logger.error("ERROR: " + err);
                setSearchError(err.message);
            }
        };
        const payload = {
            searchText: timerStartText,
            sequence: auth.sequence,
            operation: "searchForUser",
        };
        sendAjaxRequest(apiCallback, "search", payload, auth.bearer, cacheBusterStore, logger);
    };
    const searchBarChange = (searchText) => {
        logger.log("searchBarChange: " + searchText);
        if (timer) {
            logger.log("searchBarChange: cancelled timer");
            window.clearTimeout(timer);
            timer = undefined;
        }
        timerStartText = searchText;
        timer = window.setTimeout(() => {
            logger.log("searchBarChange: timeout " + searchText + " ?= " + timerStartText);
            if (searchText == timerStartText) {
                doSearch(searchText, logger);
            }
        }, 1500);
    };
    const searchBarClicked = () => {
        logger.log("searchBarClicked");
        if (timer) {
            logger.log("searchBarChange: cancelled timer");
            window.clearTimeout(timer);
            timer = undefined;
        }
        doSearch(timerStartText, logger);
    };
    const userButtonClicked = (userId) => {
        logger.log("userButtonClicked: " + userId);
        navigate("/ProfileView?userId=" + userId);
    };
    // Enhanced moderation action handlers
    const handleModerationAction = (filename, action) => {
        setModerationDialog({
            open: true,
            action,
            filename,
            loading: false,
            success: false,
            error: null,
        });
    };
    const confirmModerationAction = () => __awaiter(this, void 0, void 0, function* () {
        const { action, filename } = moderationDialog;
        if (!action || !filename)
            return;
        setModerationDialog((prev) => (Object.assign(Object.assign({}, prev), { loading: true })));
        setActionInProgress({ filename, action });
        try {
            yield new Promise((resolve, reject) => {
                setModerationReviewStatus(filename, action === "allow", // reviewStatus
                auth, cacheBusterStore, () => {
                    // Success callback
                    resolve();
                }, (message) => {
                    // Error callback
                    reject(new Error(message));
                }, logger);
            });
            // Success
            setModerationDialog((prev) => (Object.assign(Object.assign({}, prev), { loading: false, success: true, error: null })));
            // Remove the post from the list after a delay
            setTimeout(() => {
                setModerationDialog((prev) => (Object.assign(Object.assign({}, prev), { open: false })));
                setActionInProgress(null);
                // Reload moderated posts to refresh the list
                loadModerationEntries();
            }, 2000);
        }
        catch (error) {
            // Error
            setModerationDialog((prev) => (Object.assign(Object.assign({}, prev), { loading: false, success: false, error: error instanceof Error ? error.message : "Unknown error occurred" })));
            setActionInProgress(null);
        }
    });
    const closeModerationDialog = () => {
        if (!moderationDialog.loading) {
            setModerationDialog({
                open: false,
                action: null,
                filename: "",
                loading: false,
                success: false,
                error: null,
            });
            setActionInProgress(null);
        }
    };
    const searchBar = (_jsx(SearchBar, { label: "Find Friends", onSearchChanged: searchBarChange, onSearchClicked: searchBarClicked }, "searchBar"));
    const searchResultsEntries = [];
    if (searchResults) {
        searchResults.forEach((entry) => {
            if (!entry.userId || !entry.displayName) {
                return;
            }
            searchResultsEntries.push(_jsx(UserAvatar, { keyStr: entry.userId + ".search.entry.avatar", userId: entry.userId, userName: entry.displayName, onUserClicked: userButtonClicked, userNameVisible: true, auth: auth }, entry.userId + ".search.entry.avatar"));
        });
    }
    const searchResultsStack = (_jsx(ContentCardStacks, { keyStr: "search.results.stacks", cards: searchResultsEntries, columnCount: 4 }, "search.results.stacks"));
    const searchArea = (_jsxs(Stack, Object.assign({ direction: "column" }, { children: [_jsx(Divider, { orientation: "horizontal" }), _jsx(Typography, Object.assign({ variant: "body1", color: subHeadingColor, sx: {
                    width: "100%",
                    textAlign: "left",
                    marginBottom: 0,
                    paddingBottom: 0,
                    marginLeft: "5px",
                } }, { children: "Search" })), searchBar, _jsx(Divider, { orientation: "horizontal" }), searchError && (_jsx(Card, Object.assign({ elevation: 0, sx: { backgroundColor: "#FFEEEE", padding: "4", marginTop: "0.5em" }, color: "text.error" }, { children: _jsxs(CardContent, { children: [_jsx(Typography, Object.assign({ variant: "h6", component: "div", color: "error", sx: { textAlign: "center" }, gutterBottom: true }, { children: "ERROR" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: searchError }))] }, "friends-search-error-card-content") }), "friends-search-error-card")), !searchError && (_jsxs(Stack, Object.assign({ direction: "column" }, { children: [searching && _jsx(LinearProgress, { sx: { width: "100%" } }), !searching && (_jsx(Typography, Object.assign({ variant: "body1", color: subHeadingColor, sx: {
                            width: "100%",
                            textAlign: "left",
                            marginBottom: 0,
                            paddingBottom: 0,
                            marginLeft: "5px",
                        } }, { children: "Search Results" }))), !searching && searchResultsEntries.length > 0 && searchResultsStack, !searching && searchResultsEntries.length <= 0 && (_jsx(Typography, Object.assign({ sx: { display: "flex", justifyContent: "center" } }, { children: "No search results." })))] }), "search.error.stack"))] }), "searchArea"));
    const userSearchContentCard = (_jsx(Card, Object.assign({ elevation: 10, sx: {
            backgroundColor: "#FFFFFF",
            padding: layout.cardPadding,
            margin: layout.cardMargin,
            alignContent: "center",
            textAlign: "center",
        } }, { children: _jsx(CardContent, Object.assign({ sx: {
                padding: layout.cardContentPadding,
                margin: layout.cardContentMargin,
            } }, { children: searchArea }), "user.search.card.content") }), "user.search.card"));
    let reportsContent;
    if (reportsLoading) {
        reportsContent = _jsx(LinearProgress, {});
    }
    if (reportsError) {
        reportsContent = (_jsx(Card, Object.assign({ elevation: 0, sx: { backgroundColor: "#FFEEEE", padding: "4", marginTop: "0.5em" }, color: "text.error" }, { children: _jsxs(CardContent, { children: [_jsx(Typography, Object.assign({ variant: "h6", component: "div", color: "error", sx: { textAlign: "center" }, gutterBottom: true }, { children: "ERROR" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: reportsError }))] }, "reports.error.card.content") }), "reports.error.card"));
    }
    else if (reportsList) {
        const reportsElements = [];
        reportsList.forEach((entry) => {
            reportsElements.push(_jsx(ReportElement, { report: entry, auth: auth }, "report.element." + entry.reportId));
        });
        if (reportsElements.length <= 0) {
            reportsElements.push(_jsx(Typography, Object.assign({ sx: { display: "flex", justifyContent: "center", padding: "1em" } }, { children: "No reports found." }), "reports.no.elements"));
        }
        // Create the reports content with the elements
        reportsContent = (_jsxs(Stack, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "Reports" })), _jsx(ContentCardStacks, { keyStr: "reports.elements", cards: reportsElements })] }, "reports.content"));
    }
    // Moderated Posts Section
    let moderatedPostsContent;
    if (modStore.loading) {
        moderatedPostsContent = _jsx(LinearProgress, {});
    }
    else if (modStore && modStore.moderatedPosts) {
        moderatedPostsContent = (_jsxs(Stack, Object.assign({ spacing: 2 }, { children: [_jsxs(Box, Object.assign({ sx: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    } }, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "Moderated Posts" })), _jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 2 } }, { children: [_jsx(Chip, { label: modStore.includeReviewed ? "All Posts" : "Pending Review", color: modStore.includeReviewed ? "default" : "primary", size: "small" }), _jsx(Button, Object.assign({ variant: "outlined", size: "small", onClick: toggleFilter, disabled: modStore.loading }, { children: modStore.includeReviewed
                                        ? "Show Pending Only"
                                        : "Show All Posts" }))] }))] })), modStore.error && (_jsxs(Alert, Object.assign({ severity: "error", sx: { mb: 2 } }, { children: [_jsx(Typography, Object.assign({ variant: "body1", component: "div" }, { children: "Error loading moderated posts" })), _jsx(Typography, Object.assign({ variant: "body2", component: "div" }, { children: modStore.error }))] }))), modStore.moderatedPosts.length === 0 ? (_jsxs(Box, Object.assign({ sx: { textAlign: "center", py: 4 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "text.secondary" }, { children: "No posts requiring moderation" })), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "All content has been reviewed or no flagged content exists." }))] }))) : (_jsx(Stack, Object.assign({ spacing: 2 }, { children: modStore.moderatedPosts.map((post) => (_jsx(ModeratedPostCard, { moderationEntry: post, onAllow: (filename) => handleModerationAction(filename, "allow"), onBlock: (filename) => handleModerationAction(filename, "block"), actionInProgress: (actionInProgress === null || actionInProgress === void 0 ? void 0 : actionInProgress.filename) === post.filename
                            ? actionInProgress.action
                            : null }, post.filename))) }))), _jsx(ModerationActionDialog, { open: moderationDialog.open, onClose: closeModerationDialog, onConfirm: confirmModerationAction, action: moderationDialog.action, loading: moderationDialog.loading, success: moderationDialog.success, error: moderationDialog.error, filename: moderationDialog.filename })] }), "moderated.posts.content"));
    }
    else {
        moderatedPostsContent = (_jsxs(Stack, Object.assign({ spacing: 2 }, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "Moderated Posts" })), _jsx(Box, Object.assign({ sx: { textAlign: "center", py: 4 } }, { children: _jsx(Typography, Object.assign({ variant: "body1", color: "text.secondary" }, { children: "No moderated posts found." })) }))] }), "moderated.posts.content"));
    }
    // Ops Dashboard Section
    const opsDashboardContent = (_jsx(Card, Object.assign({ elevation: 10, sx: {
            backgroundColor: "#FFFFFF",
            padding: layout.cardPadding,
            margin: layout.cardMargin,
        } }, { children: _jsx(CardContent, { children: _jsxs(Stack, Object.assign({ spacing: 2 }, { children: [_jsxs(Box, Object.assign({ sx: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                        } }, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "Ops Dashboard" })), _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1 } }, { children: [!opsHtml && !opsLoading && (_jsx(Button, Object.assign({ variant: "contained", size: "small", onClick: () => loadOpsDashboard(false), disabled: opsLoading }, { children: "Load Dashboard" }))), opsHtml && (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ variant: "outlined", size: "small", startIcon: _jsx(RefreshIcon, {}), onClick: () => loadOpsDashboard(true), disabled: opsLoading }, { children: "Refresh" })), _jsx(Button, Object.assign({ variant: "outlined", size: "small", startIcon: _jsx(OpenInNewIcon, {}), onClick: openDashboardInNewTab }, { children: "Open in New Tab" }))] }))] }))] })), opsLoading && (_jsxs(Box, Object.assign({ sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: 4,
                            justifyContent: "center",
                        } }, { children: [_jsx(CircularProgress, { size: 24 }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: opsGenerating
                                    ? "Generating ops dashboard in the background — this usually takes 3-4 minutes. We'll load it automatically when it's ready."
                                    : "Loading ops dashboard..." }))] }))), opsHtml && opsGeneratedAt && !opsLoading && (_jsxs(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: ["Generated ", new Date(opsGeneratedAt).toLocaleString()] }))), opsError && (_jsx(Alert, Object.assign({ severity: "error", sx: { mb: 1 } }, { children: opsError }))), opsHtml && !opsLoading && (_jsx(Box, { component: "iframe", ref: opsIframeRef, srcDoc: opsHtml, sandbox: "allow-scripts", title: "Ops Dashboard", sx: {
                            width: "100%",
                            minHeight: "800px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                        } })), _jsx(Divider, {}), _jsx(Typography, Object.assign({ variant: "subtitle1", sx: { fontWeight: 500 } }, { children: "Error Analysis" })), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "Deep-dive analysis of errors for a specific Lambda function using AI." })), _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, alignItems: "flex-start" } }, { children: [_jsx(TextField, { size: "small", label: "Lambda function name", placeholder: "e.g. content, studio, auth", value: opsAnalyzeTarget, onChange: (e) => setOpsAnalyzeTarget(e.target.value), disabled: opsAnalyzeLoading, sx: { flexGrow: 1 }, onKeyDown: (e) => {
                                    if (e.key === "Enter")
                                        runOpsAnalyze(opsAnalyzeTarget);
                                } }), _jsx(Button, Object.assign({ variant: "contained", size: "small", startIcon: opsAnalyzeLoading ? (_jsx(CircularProgress, { size: 16, color: "inherit" })) : (_jsx(SearchIcon, {})), onClick: () => runOpsAnalyze(opsAnalyzeTarget), disabled: opsAnalyzeLoading || !opsAnalyzeTarget.trim(), sx: { minWidth: "100px", height: "40px" } }, { children: "Analyze" }))] })), opsAnalyzeError && _jsx(Alert, Object.assign({ severity: "error" }, { children: opsAnalyzeError })), opsAnalyzeResult && (_jsx(Card, Object.assign({ variant: "outlined", sx: { p: 2, backgroundColor: "#f9f9f9" } }, { children: _jsxs(Stack, Object.assign({ spacing: 1.5 }, { children: [_jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 1 } }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2" }, { children: "Severity:" })), _jsx(Chip, { label: opsAnalyzeResult.severity || "unknown", size: "small", color: opsAnalyzeResult.severity === "critical"
                                                ? "error"
                                                : opsAnalyzeResult.severity === "high"
                                                    ? "warning"
                                                    : opsAnalyzeResult.severity === "medium"
                                                        ? "info"
                                                        : "default" })] })), _jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2" }, { children: "Root Cause:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: opsAnalyzeResult.rootCause || "N/A" }))] }), opsAnalyzeResult.affectedCode &&
                                    opsAnalyzeResult.affectedCode.length > 0 && (_jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2" }, { children: "Affected Code:" })), opsAnalyzeResult.affectedCode.map((entry, idx) => (_jsxs(Typography, Object.assign({ variant: "body2", sx: {
                                                ml: 1,
                                                fontFamily: "monospace",
                                                fontSize: "0.85em",
                                            } }, { children: [entry.file, entry.lines ? ":" + entry.lines : "", " \u2014", " ", entry.description] }), "affected-code-" + idx)))] })), _jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2" }, { children: "Suggested Fix:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: opsAnalyzeResult.suggestedFix || "N/A" }))] }), _jsxs(Box, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2" }, { children: "Prevention:" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: opsAnalyzeResult.prevention || "N/A" }))] })] })) })))] })) }, "ops.dashboard.card.content") }), "ops.dashboard.card"));
    const content = (_jsxs(Stack, Object.assign({ sx: {
            padding: "2em",
            maxWidth: "1000px",
            width: "100%",
        }, spacing: 3 }, { children: [_jsx(Typography, Object.assign({ variant: "h5", color: "primary", sx: { textAlign: "center", width: "100%" } }, { children: "Admin" })), opsDashboardContent, userSearchContentCard, reportsContent, moderatedPostsContent] })));
    const output = (_jsx("div", Object.assign({ style: { width: "100%", padding: 0, margin: 0 }, id: "outer" }, { children: _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: {
                        backgroundColor: "#FFFFFF",
                        padding: 0,
                        margin: 0,
                        display: "flex",
                        justifyContent: "center",
                    } }, { children: content })), _jsx(Footer, {})] })) })));
    return output;
}
